import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, DotsHorizontalIcon } from '@radix-ui/react-icons';

const IssueHeader = ({
  issue,
  statusColor,
  onBack,
  onDelete,
  isDeleting,
  isDeleteError,
  deleteError
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete();
      setIsMenuOpen(false);
    }
  };

  const issueLinks = issue?.fields?.issuelinks || [];

  const childrenIssues = issueLinks
    .filter(link => link.inwardIssue)
    .map(link => link.inwardIssue);

  const parentIssues = issueLinks
    .filter(link => link.outwardIssue)
    .map(link => link.outwardIssue);

  const showMenu = onDelete || parentIssues.length > 0 || childrenIssues.length > 0;

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <button
            onClick={onBack}
            className="inline-flex items-center text-black hover:text-black transition-colors font-medium gap-2"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {t('Back to tickets')}
          </button>
        </div>
        <div className="flex items-center gap-4">
          {showMenu && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors cursor-pointer relative"
              disabled={isDeleting}
            >
              <DotsHorizontalIcon className="w-5 h-5" />
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10 py-1">

                  {/* PARENT */}
                  {parentIssues.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 bg-gray-50/50">
                        {t('Parent')}
                      </div>
                      {parentIssues.map((parent) => (
                        <a
                          key={parent.key || parent.id}
                          href={`/issues/${parent.key}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 truncate"
                          title={parent.fields?.summary}
                        >
                          <span className="font-medium">{parent.key}</span>
                          {parent.fields?.summary && <span className="text-gray-500"> - {parent.fields.summary}</span>}
                        </a>
                      ))}
                      {(childrenIssues.length > 0 || onDelete) && <div className="border-t border-gray-100 my-1"></div>}
                    </>
                  )}

                  {/* CHILDREN */}
                  {childrenIssues.length > 0 && (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 bg-gray-50/50">
                        {t('Children')}
                      </div>
                      {childrenIssues.map((child) => (
                        <a
                          key={child.key || child.id}
                          href={`/issues/${child.key}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 truncate"
                          title={child.fields?.summary}
                        >
                          <span className="font-medium">{child.key}</span>
                          {child.fields?.summary && <span className="text-gray-500"> - {child.fields.summary}</span>}
                        </a>
                      ))}
                      {onDelete && <div className="border-t border-gray-100 my-1"></div>}
                    </>
                  )}

                  {/* ACTIONS */}
                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick();
                      }}
                      disabled={isDeleting}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        isDeleting
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:bg-red-50'
                      } transition-colors`}
                    >
                      {isDeleting ? t('Deleting…') : t('Delete ticket')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">{issue?.key}</h1>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}>
            {issue?.status}
          </span>
        </div>
      </div>

      {isDeleteError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
          {t('Error while deleting: {{error}}', { error: deleteError?.message || t('An error occurred') })}
        </div>
      )}

      {issue?.summary && (
        <p className="mt-2 text-gray-700 text-sm sm:text-base leading-relaxed">{issue.summary}</p>
      )}
    </div>
  );
}

export default IssueHeader;


